import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiCountry, ApiCustomField, ApiEnumGroup, ApiFunctionFull } from '@agunity/api-v4/build/data-contracts';
import { RootState } from '../store';

export interface StaffState {
  isDirty_Staff_Personal: boolean;
  staff_Redirect_URL: string;
  isOpenUnsavedModal: boolean;
  isOpenSupportModal: boolean;
  tagList: ApiCustomField[];
  countryOptions: ApiCountry[] | [];
  functionStructureList: ApiFunctionFull[];
  isOpenViewAvatar: boolean;
  enumList: ApiEnumGroup[];
  addNewOrgInfo: { name: string, id: string };
  isToastOpen_navFromAddStaff: boolean;
  nameCheck: { open: boolean, message: string };
  emailExists: boolean;
  addNewStaffFuncAndRoles: { functionId: number, id: number, name: string } | undefined;
  allRolesUnderOrg: ({id: number, name: string, description: string} | undefined)[];
  selectedRoles: number[];
  editStaffFullName: string;
}

const initialState: StaffState = {
  isDirty_Staff_Personal: false,
  staff_Redirect_URL: '',
  isOpenUnsavedModal: false,
  isOpenSupportModal: false,
  tagList: [],
  countryOptions: [],
  functionStructureList: [],
  isOpenViewAvatar: false,
  enumList: [],
  addNewOrgInfo: { name: '', id: '' },
  isToastOpen_navFromAddStaff: false,
  nameCheck: { open: false, message: '' },
  emailExists: false,
  addNewStaffFuncAndRoles: undefined,
  allRolesUnderOrg: [],
  selectedRoles: [],
  editStaffFullName: ''
};

export const StaffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    setIsDirty_Staff_Personal: (
      state: StaffState,
      action: PayloadAction<boolean>
    ) => {
      state.isDirty_Staff_Personal = action.payload;
    },
    setStaff_Redirect_URL: (
      state: StaffState,
      action: PayloadAction<string>
    ) => {
      state.staff_Redirect_URL = action.payload;
    },
    setIsOpenUnsavedModal: (
      state: StaffState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenUnsavedModal = action.payload;
    },
    setIsOpenSupportModal: (
      state: StaffState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenSupportModal = action.payload;
    },
    setStaff_TagList: (
      state: StaffState,
      action: PayloadAction<ApiCustomField[]>
    ) => {
      state.tagList = action.payload;
    },
    setStaff_CountryOptions: (
      state: StaffState,
      action: PayloadAction<ApiCountry[] | []>
    ) => {
      state.countryOptions = action.payload;
    },
    setFunctionStructureList: (
      state: StaffState,
      action: PayloadAction<ApiFunctionFull[]>
    ) => {
      state.functionStructureList = action.payload;
    },
    setIsOpenViewAvatar: (
      state: StaffState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenViewAvatar = action.payload;
    },
    setStaff_EnumList: (
      state: StaffState,
      action: PayloadAction<ApiEnumGroup[]>
    ) => {
      state.enumList = action.payload;
    },
    setAddNewOrgInfo: (
      state: StaffState,
      action: PayloadAction<{ name: string, id: string }>
    ) => {
      state.addNewOrgInfo = action.payload;
    },
    setIsToastOpen_navFromAddStaff: (
      state: StaffState,
      action: PayloadAction<boolean>
    ) => {
      state.isToastOpen_navFromAddStaff = action.payload;
    },
    setNameCheck: (
      state: StaffState,
      action: PayloadAction<{ open: boolean, message: string }>
    ) => {
      state.nameCheck = action.payload;
    },
    setEmailExists: (
      state: StaffState,
      action: PayloadAction<boolean>
    ) => {
      state.emailExists = action.payload;
    },
    setAddNewStaffFuncAndRoles: (
      state: StaffState,
      action: PayloadAction<{ functionId: number, id: number, name: string } | undefined>
    ) => {
      state.addNewStaffFuncAndRoles = action.payload;
    },
    setAllRolesUnderOrg: (
      state: StaffState,
      action: PayloadAction<({id: number, name: string, description: string} | undefined)[]>
    ) => {
      state.allRolesUnderOrg = action.payload;
    },
    setSelectedRoles: (
      state: StaffState,
      action: PayloadAction<number[]>
    ) => {
      state.selectedRoles = action.payload;
    },
    setEditStaffFullName: (
      state: StaffState,
      action: PayloadAction<string>
    ) => {
      state.editStaffFullName = action.payload;
    },
    clearStaffCache: (
      state: StaffState,
    ) => {
      Object.assign(state, initialState);
    },
  }
});

export const {
  setIsDirty_Staff_Personal,
  setStaff_Redirect_URL,
  setIsOpenUnsavedModal,
  setIsOpenSupportModal,
  setStaff_TagList,
  setStaff_CountryOptions,
  setFunctionStructureList,
  setIsOpenViewAvatar,
  setStaff_EnumList,
  setAddNewOrgInfo,
  setIsToastOpen_navFromAddStaff,
  setNameCheck,
  setEmailExists,
  setAddNewStaffFuncAndRoles,
  setAllRolesUnderOrg,
  setSelectedRoles,
  setEditStaffFullName,
  clearStaffCache
} = StaffSlice.actions;

export const selectIsOpenViewAvatar = (state: RootState) => state.staff.isOpenViewAvatar;

export default StaffSlice.reducer;
